// 022 — Administración de Medicamentos / Kardex (SNS-MSP/HCU-form.022/2021,
// docs/msp/). Grilla: MedicationRequest ACTIVOS **de la estancia de
// internación abierta** (2026-09-13: antes eran todos los `status=active` del
// paciente, y entraban recetas ambulatorias que nadie cierra nunca — ver
// `prescripcionesDeEstancia` en enfermeria.ts) × horas del día
// de enfermería (8..7, mismo orden que el HCU-form.120). Cada celda registra
// un MedicationAdministration (completed, o not-done + statusReason) con
// dosis/vía, performer (responsable), effectiveDateTime y request → la
// prescripción. Arriba, "Alergia a medicamentos" desde las AllergyIntolerance
// activas (sin categoría en este sistema: se listan todas).
//
// Compatibilidad: los MedicationAdministration del 022 viejo (sin performer
// ni dosage, un solo registro por clic) se ubican igual por request + hora;
// los de prescripciones que ya no están activas se listan debajo de la grilla.
import type { AllergyIntolerance, Encounter, MedicationAdministration, MedicationRequest, Reference } from '@medplum/fhirtypes';
import { normalizeErrorString } from '@medplum/core';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { FormularioDiscretoProps, Par } from './index';
import { CampoDictado } from './CampoDictado';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional, recursoRespuesta } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  celdaDeAdministracion,
  construirAdministracion,
  diaEnfermeriaDe,
  etiquetaMedicamento,
  fechaDeCelda,
  HORAS_DEL_DIA,
  pautaDe,
  prescripcionesDeEstancia,
  rangoDiaEnfermeria,
  responsableDe,
} from './enfermeria';
import { buscarEstanciaAbierta } from '../../paciente/asegurarEncounterDeHoy';
import { Responsable, SelectorDiaEnfermeria } from './SelectorTurno';
import { descripcionesAlergiaMedicamentos, diasHoja022, paresAlergiaMedicamentos, paresKardexHoja, type TextosKardex } from './kardex022Datos';
import { useResponsable } from './useResponsable';

interface Pendiente {
  readonly clave: string;
  readonly request: MedicationRequest;
  readonly hora: number;
  readonly minuto: number;
  readonly administrado: boolean;
  readonly motivo: string;
  readonly dosis: string;
  readonly via: string;
}

type Editor = { request: MedicationRequest; hora: number };

function hhmm(hora: number, minuto: number): string {
  return `${String(hora).padStart(2, '0')}:${String(minuto).padStart(2, '0')}`;
}

function horaDe(fechaIso: string | undefined): string {
  if (!fechaIso) return '—';
  const f = new Date(fechaIso);
  return hhmm(f.getHours(), f.getMinutes());
}

function EditorCelda({ editor, onAgregar, onCerrar }: { readonly editor: Editor | null; readonly onAgregar: (p: Omit<Pendiente, 'clave' | 'request'>) => void; readonly onCerrar: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [administrado, setAdministrado] = useState(true);
  const [hora, setHora] = useState('');
  const [motivo, setMotivo] = useState('');
  const [dosis, setDosis] = useState('');
  const [via, setVia] = useState('');

  useEffect(() => {
    if (!editor) return;
    const ahora = new Date();
    setAdministrado(true);
    setHora(hhmm(editor.hora, ahora.getHours() === editor.hora ? ahora.getMinutes() : 0));
    setMotivo('');
    setDosis('');
    setVia('');
  }, [editor]);

  const [hh, mm] = hora.split(':').map(Number);
  const horaValida = Number.isInteger(hh) && Number.isInteger(mm);
  const puedeAgregar = horaValida && (administrado || motivo.trim() !== '');

  return (
    <Dialog open={editor !== null} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('formularios.f022.registrarTitulo')}</DialogTitle>
        </DialogHeader>
        {editor && (
          <div className="flex flex-col gap-3">
            <p className="text-sm">
              <strong>{etiquetaMedicamento(editor.request)}</strong>
              {pautaDe(editor.request) && <span className="text-muted-foreground"> — {pautaDe(editor.request)}</span>}
            </p>
            <fieldset className="flex gap-2 min-w-0 border-0 p-0" aria-label={t('formularios.f022.estado')}>
              <Button type="button" size="sm" aria-pressed={administrado} variant={administrado ? 'default' : 'outline'} onClick={() => setAdministrado(true)}>
                {t('formularios.f022.administrado')}
              </Button>
              <Button type="button" size="sm" aria-pressed={!administrado} variant={!administrado ? 'default' : 'outline'} onClick={() => setAdministrado(false)}>
                {t('formularios.f022.noAdministrado')}
              </Button>
            </fieldset>
            <div className="flex flex-col gap-1">
              <Label htmlFor="f022-hora">{t('formularios.f022.hora')}</Label>
              <Input id="f022-hora" type="time" className="w-fit" value={hora} onChange={(e) => setHora(e.target.value)} />
            </div>
            {!administrado && <CampoDictado id="f022-motivo" etiqueta={t('formularios.f022.motivo')} valor={motivo} onChange={setMotivo} />}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <Label htmlFor="f022-dosis">{t('formularios.f022.dosis')}</Label>
                <Input id="f022-dosis" placeholder="500 mg" value={dosis} onChange={(e) => setDosis(e.target.value)} />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="f022-via">{t('formularios.f022.via')}</Label>
                <Input id="f022-via" placeholder="Oral / IV / IM / SC" value={via} onChange={(e) => setVia(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                disabled={!puedeAgregar}
                onClick={() => {
                  onAgregar({ hora: hh!, minuto: mm!, administrado, motivo: motivo.trim(), dosis: dosis.trim(), via: via.trim() });
                  onCerrar();
                }}
              >
                {t('formularios.f022.agregar')}
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={onCerrar}>
                {t('formularios.f022.cancelar')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// El `encounter` genérico de FormularioDiscretoProps (el `?encounter=` más
// reciente de la URL, AMB o IMP) NO se usa acá a propósito: la grilla ya se
// filtra por `estancia` (la internación IMP abierta, `buscarEstanciaAbierta`
// abajo) — si el paciente tiene una consulta AMB y una internación IMP
// abiertas A LA VEZ, el `encounter` genérico podría ser la AMB (la más
// reciente) mientras la grilla sigue mostrando las prescripciones de la
// internación. Guardar contra ese `encounter` habría dejado el
// `MedicationAdministration` enlazado a un Encounter DISTINTO del que el
// Kardex usó para decidir qué mostrar — mismo paciente, sin fuga, pero
// desalineado (hallazgo del revisor, 2026-09-14/16). Se guarda siempre
// contra `estancia` (puede quedar `undefined` sin internación abierta,
// igual que antes).
/** Administraciones que se leen para la hoja de 4 días; si la lectura llega a este número no se guarda (podría estar truncada). */
const TOPE_LECTURA_HOJA = 1000;

export function Formulario022({ subject, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const responsable = useResponsable();
  const [camposEncabezado, setCamposEncabezado] = useState(() => valoresInicialesEncabezadoMsp());
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, camposEncabezado, '022', t('hub.formulario.022'), {
    // Kardex de la estancia de internación abierta: nunca es consulta externa.
    servicio: 'Hospitalización',
  });
  const pacienteRef = subject.reference ?? '';
  const [dia, setDia] = useState(() => diaEnfermeriaDe(new Date()));
  const [activas, setActivas] = useState<MedicationRequest[]>();
  /** `undefined` = todavía cargando; `null` = no hay internación abierta. */
  const [estancia, setEstancia] = useState<Encounter | null>();
  const [alergias, setAlergias] = useState<AllergyIntolerance[]>();
  const [administraciones, setAdministraciones] = useState<MedicationAdministration[]>([]);
  /** Día de enfermería cuyas administraciones se leyeron BIEN (null = cargando, cambió el día o falló la lectura). El PDF firmado no se arma sin esto. */
  const [diaCargado, setDiaCargado] = useState<string | null>(null);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [pendientes, setPendientes] = useState<Pendiente[]>([]);
  const [editor, setEditor] = useState<Editor | null>(null);
  const { guardando, error, guardar } = useGuardado();

  useEffect(() => {
    let cancelado = false;
    const patientId = pacienteRef.split('/')[1] ?? '';
    Promise.all([
      medplum.searchResources('MedicationRequest', { patient: pacienteRef, status: 'active', _count: 100 }),
      medplum.searchResources('AllergyIntolerance', { patient: pacienteRef, 'clinical-status': 'active', _count: 50 }),
      // Solo lectura: Enfermería nunca abre una internación (ver
      // asegurarEncounterDeHoy.ts). Si no hay ninguna, la grilla lo dice.
      buscarEstanciaAbierta(medplum, patientId),
    ])
      .then(([mrs, als, imp]) => {
        if (cancelado) return;
        setActivas(mrs as MedicationRequest[]);
        setAlergias(als as AllergyIntolerance[]);
        setEstancia(imp);
      })
      .catch((err: unknown) => !cancelado && setErrorCarga(normalizeErrorString(err)));
    return () => {
      cancelado = true;
    };
  }, [medplum, pacienteRef]);

  useEffect(() => {
    let cancelado = false;
    setDiaCargado(null);
    const { inicio, fin } = rangoDiaEnfermeria(dia);
    // effective-time repetido (ge/lt): query string a mano — ver referencia_gotchas_fhir_medplum.
    medplum
      .searchResources(
        'MedicationAdministration',
        `patient=${encodeURIComponent(pacienteRef)}&effective-time=ge${encodeURIComponent(inicio.toISOString())}&effective-time=lt${encodeURIComponent(fin.toISOString())}&status:not=entered-in-error&_count=500`,
      )
      .then((mas) => {
        if (cancelado) return;
        setAdministraciones(mas as MedicationAdministration[]);
        setDiaCargado(dia);
      })
      .catch((err: unknown) => !cancelado && setErrorCarga(normalizeErrorString(err)));
    return () => {
      cancelado = true;
    };
  }, [medplum, pacienteRef, dia]);

  const porCelda = useMemo(() => {
    const mapa = new Map<string, MedicationAdministration[]>();
    for (const ma of administraciones) {
      const celda = celdaDeAdministracion(ma);
      if (!celda) continue;
      const clave = `${celda.requestId}|${celda.hora}`;
      mapa.set(clave, [...(mapa.get(clave) ?? []), ma]);
    }
    return mapa;
  }, [administraciones]);

  const estanciaRef = estancia?.id ? `Encounter/${estancia.id}` : undefined;
  const estanciaReference = useMemo<Reference<Encounter> | undefined>(() => (estanciaRef ? { reference: estanciaRef } : undefined), [estanciaRef]);
  const prescripciones = useMemo(
    () => (activas === undefined || estancia === undefined ? undefined : prescripcionesDeEstancia(activas, estanciaRef)),
    [activas, estancia, estanciaRef],
  );

  const idsActivos = new Set((prescripciones ?? []).map((mr) => mr.id));
  const deNoActivas = administraciones.filter((ma) => {
    const id = celdaDeAdministracion(ma)?.requestId;
    return !id || !idsActivos.has(id);
  });

  const listaAlergias = descripcionesAlergiaMedicamentos(alergias ?? []);
  const textoAlergia = listaAlergias.length > 0 ? `${t('formularios.f022.alergiaSi')} — ${listaAlergias.join(', ')}` : t('formularios.f022.alergiaNo');
  const textosKardex: TextosKardex = {
    alergiaPregunta: t('formularios.f022.alergiaMedicamentos'),
    alergiaSi: t('formularios.f022.alergiaSi'),
    alergiaNo: t('formularios.f022.alergiaNo'),
    alergiaDescriba: t('formularios.f022.alergiaDescriba'),
    administrado: t('formularios.f022.administrado'),
    noAdministrado: t('formularios.f022.noAdministrado'),
    sinAdministraciones: t('formularios.f022.sinAdministraciones'),
    responsable: t('enfermeria.responsable'),
    otrasAdministraciones: t('formularios.f022.otrasAdministraciones'),
    hoja: t('formularios.f022.hoja'),
    dia: t('formularios.f022.dia'),
  };

  const handleSubmit = (): Promise<void> => guardar(async () => {
    if (pendientes.length === 0) throw new Error(t('formularios.f022.sinPendientes'));
    // El responsable de cada administración es obligatorio (el papel lo imprime junto a la hora): sin un profesional identificado en la sesión no se guarda.
    if (!responsable?.display) throw new Error(t('formularios.f022.sinResponsable'));
    // El PDF imprime el Kardex de la hoja: sin haber leído bien las administraciones ya guardadas saldría incompleto ("sin administraciones" para dosis que sí existen).
    if (diaCargado !== dia || prescripciones === undefined) throw new Error(t('formularios.f022.administracionesSinCargar'));
    // Si no se pudieron leer las alergias, el PDF diría "No" sin saberlo: no se guarda hasta poder verificarlas.
    if (alergias === undefined) throw new Error(t('formularios.f022.alergiasSinVerificar'));
    // El PDF imprime la hoja de 4 días de enfermería que termina en `dia` (como el papel): se leen ANTES de guardar (si falla la lectura no se
    // guarda nada; leer después del guardado dejaría el lote confirmado sin PDF y el reintento duplicaría dosis).
    const diasDeHoja = diasHoja022(dia);
    const hoja = await medplum
      .searchResources(
        'MedicationAdministration',
        `patient=${encodeURIComponent(pacienteRef)}&effective-time=ge${encodeURIComponent(rangoDiaEnfermeria(diasDeHoja[0]!).inicio.toISOString())}&effective-time=lt${encodeURIComponent(rangoDiaEnfermeria(dia).fin.toISOString())}&status:not=entered-in-error&_count=${TOPE_LECTURA_HOJA}`,
      )
      .catch(() => undefined);
    // Al llegar al tope la búsqueda pudo truncarse en silencio: se prefiere no guardar antes que firmar un Kardex al que le faltan dosis.
    if (hoja === undefined || hoja.length >= TOPE_LECTURA_HOJA) throw new Error(t('formularios.f022.hojaSinCargar'));
    // Una sola transacción: si Medplum rechaza una a mitad de camino no se guarda ninguna
    // (un "administrado" duplicado por reintento parecería una dosis doble).
    const guardados: Pendiente[] = pendientes;
    const lote = new LoteTransaccional();
    const locales = pendientes.map((p) =>
      lote.crear<MedicationAdministration>(
        construirAdministracion({
          request: p.request,
          subject,
          encounter: estanciaReference,
          fecha: fechaDeCelda(dia, p.hora, p.minuto),
          administrado: p.administrado,
          motivo: p.motivo,
          dosis: p.dosis,
          via: p.via,
          performer: responsable,
        }),
      ),
    );
    const respuesta = await lote.enviar(medplum);
    const nuevas = locales.map((local, i) => recursoRespuesta(respuesta, i, local));
    setAdministraciones((prev) => [...prev, ...nuevas]);
    const claves = new Set(guardados.map((p) => p.clave));
    setPendientes((prev) => prev.filter((x) => !claves.has(x.clave)));
    // PDF firmado: alergia SI/NO + Describa, y el Kardex COMPLETO del día (lo ya guardado + este guardado), con fecha real y responsable.
    const pares: Par[] = [
      ...paresAlergiaMedicamentos(textosKardex, listaAlergias),
      { pregunta: t('enfermeria.diaEnfermeria'), respuesta: dia },
      // Solo lo de ESTA estancia (una administración de otra internación/consulta no es parte de este Kardex); sin `context` (022 viejo) entra.
      ...paresKardexHoja(prescripciones, [...(hoja as MedicationAdministration[]), ...nuevas].filter((ma) => !estanciaRef || !ma.context?.reference || ma.context.reference === estanciaRef), diasDeHoja, textosKardex),
    ];
    onGuardado(pares, datosEncabezadoMsp);
  });

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoMspCampos subject={subject} value={camposEncabezado} onChange={setCamposEncabezado} />

      <Alert variant={listaAlergias.length > 0 ? 'red' : undefined}>
        <AlertTitle>{t('formularios.f022.alergiaMedicamentos')}</AlertTitle>
        <AlertDescription>{alergias === undefined ? t('fichaPaciente.cargando') : textoAlergia}</AlertDescription>
      </Alert>

      <div className="flex flex-wrap items-end gap-4">
        <SelectorDiaEnfermeria id="f022-dia" dia={dia} onChange={setDia} deshabilitado={pendientes.length > 0} />
        <Responsable display={responsable?.display} />
        {estancia?.period?.start && (
          <p className="text-xs text-muted-foreground">
            {t('formularios.f022.estanciaAbierta', { desde: new Date(estancia.period.start).toLocaleDateString('es-EC') })}
          </p>
        )}
      </div>

      {errorCarga && (
        <Alert variant="red">
          <AlertDescription>{t('formularios.f022.errorCarga', { error: errorCarga })}</AlertDescription>
        </Alert>
      )}

      {prescripciones === undefined ? (
        <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>
      ) : estancia === null ? (
        <Alert>
          <AlertTitle>{t('formularios.f022.titulo')}</AlertTitle>
          <AlertDescription>{t('formularios.f022.sinEstancia')}</AlertDescription>
        </Alert>
      ) : prescripciones.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('formularios.f022.sinPrescripciones')}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-muted/50">
                <th scope="col" className="sticky left-0 z-10 min-w-[12rem] border-b border-border bg-muted p-2 text-left font-semibold">
                  {t('formularios.f022.medicamento')}
                </th>
                {HORAS_DEL_DIA.map((h) => (
                  <th key={h} scope="col" className="min-w-[4.5rem] border-b border-l border-border p-1 text-center font-mono">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {prescripciones.map((mr) => (
                <tr key={mr.id} className="align-top">
                  <th scope="row" className="sticky left-0 z-10 border-b border-border bg-card p-2 text-left font-normal">
                    <span className="block font-medium">{etiquetaMedicamento(mr)}</span>
                    {pautaDe(mr) && <span className="block text-muted-foreground">{pautaDe(mr)}</span>}
                  </th>
                  {HORAS_DEL_DIA.map((h) => {
                    const guardadas = porCelda.get(`${mr.id}|${h}`) ?? [];
                    const enEspera = pendientes.filter((p) => p.request.id === mr.id && p.hora === h);
                    return (
                      <td key={h} className="border-b border-l border-border p-1">
                        <div className="flex flex-col gap-1">
                          {guardadas.map((ma) => (
                            <span key={ma.id} className="flex flex-col" title={ma.statusReason?.[0]?.text}>
                              <Badge variant={ma.status === 'not-done' ? 'destructive' : 'pine'} className="w-fit font-mono text-[10px]">
                                {ma.status === 'not-done' ? '✗' : '✓'} {horaDe(ma.effectiveDateTime)}
                              </Badge>
                              <span className="max-w-[6rem] truncate text-[10px] text-muted-foreground">{responsableDe(ma.performer)}</span>
                            </span>
                          ))}
                          {enEspera.map((p) => (
                            <span key={p.clave} className="flex items-center gap-1">
                              <Badge variant="outline" className="font-mono text-[10px]">
                                {p.administrado ? '✓' : '✗'} {hhmm(p.hora, p.minuto)}
                              </Badge>
                              <button
                                type="button"
                                className="text-[10px] text-muted-foreground hover:text-foreground"
                                aria-label={t('formularios.f022.quitar')}
                                onClick={() => setPendientes((prev) => prev.filter((x) => x.clave !== p.clave))}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          <button
                            type="button"
                            className="w-fit rounded px-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label={`${t('formularios.f022.registrarTitulo')}: ${etiquetaMedicamento(mr)} ${h}:00`}
                            onClick={() => setEditor({ request: mr, hora: h })}
                          >
                            +
                          </button>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deNoActivas.length > 0 && (
        <div className="flex flex-col gap-1">
          <h5 className="text-sm font-semibold">{t('formularios.f022.otrasAdministraciones')}</h5>
          <ul className="text-sm">
            {deNoActivas.map((ma) => (
              <li key={ma.id}>
                {horaDe(ma.effectiveDateTime)} — {ma.medicationCodeableConcept?.text ?? ma.medicationReference?.display ?? '—'} ·{' '}
                {ma.status === 'not-done' ? t('formularios.f022.noAdministrado') : t('formularios.f022.administrado')} · {responsableDe(ma.performer)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <EditorCelda
        editor={editor}
        onCerrar={() => setEditor(null)}
        onAgregar={(p) => editor && setPendientes((prev) => [...prev, { ...p, request: editor.request, clave: crypto.randomUUID() }])}
      />

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando || pendientes.length === 0}>
        {t('formularios.f022.guardarPendientes', { count: pendientes.length })}
      </Button>
    </div>
  );
}
